import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Wrench } from "lucide-react";
import ForgotPasswordDialog from "../components/ForgotPasswordDialog";

export default function Login({ onLogin }) {
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const attemptLogin = async (retryCount = 0) => {
      try {
        const response = await axios.post("/auth/login", loginData);
        toast.success("Login successful!");
        onLogin(response.data.user, response.data.access_token);
      } catch (error) {
        // Retry on network errors, not on auth errors
        if (!error.response && retryCount < 2) {
          console.log(`Login retry attempt ${retryCount + 1}`);
          await new Promise(r => setTimeout(r, 1000));
          return attemptLogin(retryCount + 1);
        }
        toast.error(error.response?.data?.detail || "Login failed - please try again");
      } finally {
        setLoading(false);
      }
    };
    
    await attemptLogin();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center items-center gap-3 mb-4">
            <Wrench className="w-12 h-12 text-white" />
            <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'Inter' }}>BBM Appliances</h1>
          </div>
          <p className="text-white/90 text-lg font-medium">Part Management System</p>
        </div>

        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle>Welcome Back</CardTitle>
            <CardDescription>Sign in to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-username">Username or Email</Label>
                <Input
                  id="login-username"
                  data-testid="login-username-input"
                  type="text"
                  placeholder="Enter username or email"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  data-testid="login-password-input"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => setForgotPasswordOpen(true)}
                  className="text-sm text-violet-600 hover:text-violet-700 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
        
        <ForgotPasswordDialog 
          open={forgotPasswordOpen} 
          onOpenChange={setForgotPasswordOpen} 
        />
      </div>
    </div>
  );
}
